import React, { useState, useEffect } from 'react';
import api from '../../../shared/utils/api';

interface TemplateLedgerReceiptItemFormData {
  Description: string | null;
  ToAmount: number | null;
  DebitAccountId: string | null;
  CreditAccountId: string | null;
  VATAccountId: string | null;
  CityTaxAccountId: string | null;
  CashflowCategoryId: string | null;
  BusinessClassOid: string | null;
  CustomerOid: string | null;
  BusinessCustomerOid: string | null;
}

interface TemplateLedgerReceiptItemPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  itemData?: any; // TemplateLedgerReceiptItem data for editing
  ledgerTemplateReceiptId?: string; // For creating new items
}

const TemplateLedgerReceiptItemPopup: React.FC<TemplateLedgerReceiptItemPopupProps> = ({
  isVisible,
  onClose,
  onSuccess,
  itemData,
  ledgerTemplateReceiptId,
}) => {
  const [formData, setFormData] = useState<TemplateLedgerReceiptItemFormData>({
    Description: null,
    ToAmount: null,
    DebitAccountId: null,
    CreditAccountId: null,
    VATAccountId: null,
    CityTaxAccountId: null,
    CashflowCategoryId: null,
    BusinessClassOid: null,
    CustomerOid: null,
    BusinessCustomerOid: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<Array<{ Oid: string; Code: string; Name: string }>>([]);
  const [cashflowCategories, setCashflowCategories] = useState<Array<{ Oid: string; Code: string; Name: string }>>([]);
  const [businessClasses, setBusinessClasses] = useState<Array<{ Oid: string; Name: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ Oid: string; CustomerName: string; CustomerID: string }>>([]);
  const [businessCustomers, setBusinessCustomers] = useState<Array<{ Code: string; Name: string }>>([]);

  useEffect(() => {
    if (isVisible) {
      fetchDropdownData();
      // If itemData is provided, populate form for editing
      if (itemData) {
        setFormData({
          Description: itemData.Description || null,
          ToAmount: itemData.ToAmount || null,
          DebitAccountId: itemData.DebitAccountId || null,
          CreditAccountId: itemData.CreditAccountId || null,
          VATAccountId: itemData.VATAccountId || null,
          CityTaxAccountId: itemData.CityTaxAccountId || null,
          CashflowCategoryId: itemData.CashflowCategoryId || null,
          BusinessClassOid: itemData.BusinessClassOid || null,
          CustomerOid: itemData.CustomerOid || null,
          BusinessCustomerOid: itemData.BusinessCustomerOid || null,
        });
      } else {
        // Reset form for new item
        setFormData({
          Description: null,
          ToAmount: null,
          DebitAccountId: null,
          CreditAccountId: null,
          VATAccountId: null,
          CityTaxAccountId: null,
          CashflowCategoryId: null,
          BusinessClassOid: null,
          CustomerOid: null,
          BusinessCustomerOid: null,
        });
      }
    }
  }, [isVisible, itemData]);

  const fetchDropdownData = async () => {
    try {
      // Fetch Accounts list
      const accountsResult = await api.get('/api/account?isActive=true');
      if (accountsResult?.success && accountsResult?.data) {
        setAccounts(accountsResult.data.map((item: any) => ({
          Oid: item.Oid,
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }

      // Fetch CashflowCategory list
      try {
        const cashflowResult = await api.get('/api/cashflow-category');
        if (cashflowResult?.success && cashflowResult?.data) {
          setCashflowCategories(cashflowResult.data.map((item: any) => ({
            Oid: item.Oid,
            Code: item.Code || '',
            Name: item.Name || ''
          })));
        }
      } catch (err) {
        console.warn('CashflowCategory API not available:', err);
      }

      // Fetch BusinessClass list
      try {
        const businessClassResult = await api.get('/api/BusinessClassOid');
        if (businessClassResult?.success && businessClassResult?.data) {
          setBusinessClasses(businessClassResult.data.map((item: any) => ({
            Oid: item.Oid || item.BusinessClassOid,
            Name: item.BusinessClassName || item.Name || ''
          })));
        }
      } catch (err) {
        console.warn('BusinessClass API not available:', err);
      }

      // Fetch Customers list
      try {
        const customersResult = await api.get('/api/customerslist');
        if (customersResult?.success && customersResult?.data) {
          setCustomers(customersResult.data.map((item: any) => ({
            Oid: item.Oid,
            CustomerName: item.CustomerName || '',
            CustomerID: item.CustomerID || ''
          })));
        }
      } catch (err) {
        console.warn('Customers API not available:', err);
      }

      // Fetch BusinessCustomer list
      try {
        const businessCustomersResult = await api.get('/api/business-customer-list');
        if (businessCustomersResult?.success && businessCustomersResult?.data) {
          setBusinessCustomers(businessCustomersResult.data.map((item: any) => ({
            Code: item.Code || '',
            Name: item.Name || ''
          })));
        }
      } catch (err) {
        console.warn('BusinessCustomer API not available:', err);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const handleInputChange = (field: keyof TemplateLedgerReceiptItemFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? null : value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        Description: formData.Description,
        ToAmount: formData.ToAmount !== null && formData.ToAmount !== undefined ? parseFloat(formData.ToAmount.toString()) : null,
        DebitAccountId: formData.DebitAccountId || null,
        CreditAccountId: formData.CreditAccountId || null,
        VATAccountId: formData.VATAccountId || null,
        CityTaxAccountId: formData.CityTaxAccountId || null,
        CashflowCategoryId: formData.CashflowCategoryId || null,
        BusinessClassOid: formData.BusinessClassOid || null,
        CustomerOid: formData.CustomerOid || null,
        BusinessCustomerOid: formData.BusinessCustomerOid || null,
      };

      let result;
      if (itemData && itemData.Oid) {
        // Update existing item
        result = await api.put(`/api/template-ledger-receipt-item?Oid=${itemData.Oid}`, submitData);
      } else {
        // Create new item
        if (!ledgerTemplateReceiptId) {
          setError('LedgerTemplateReceiptId шаардлагатай');
          setLoading(false);
          return;
        }
        result = await api.post('/api/template-ledger-receipt-item', {
          ...submitData,
          LedgerTemplateReceiptId: ledgerTemplateReceiptId
        });
      }

      if (result?.success) {
        // Call onSuccess to refresh table data
        onSuccess?.();
        onClose();
      } else {
        setError(result?.message || 'Мэдээлэл хадгалахад алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error submitting TemplateLedgerReceiptItem:', error);
      setError(error.response?.data?.message || error.message || 'Мэдээлэл хадгалахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      Description: null,
      ToAmount: null,
      DebitAccountId: null,
      CreditAccountId: null,
      VATAccountId: null,
      CityTaxAccountId: null,
      CashflowCategoryId: null,
      BusinessClassOid: null,
      CustomerOid: null,
      BusinessCustomerOid: null,
    });
    setError('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            {itemData ? 'Гүйлгээний загварын дэлгэрэнгүй засах' : 'Гүйлгээний загварын дэлгэрэнгүй нэмэх'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Тайлбар
              </label>
              <input
                type="text"
                value={formData.Description || ''}
                onChange={(e) => handleInputChange('Description', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* ToAmount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дүн
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.ToAmount ?? ''}
                onChange={(e) => handleInputChange('ToAmount', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            {/* CashflowCategory */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Мөнгөн урсгалын ангилал
              </label>
              <select
                value={formData.CashflowCategoryId || ''}
                onChange={(e) => handleInputChange('CashflowCategoryId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {cashflowCategories.map((category) => (
                  <option key={category.Oid} value={category.Oid}>
                    {category.Code} - {category.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* DebitAccount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дт. Данс
              </label>
              <select
                value={formData.DebitAccountId || ''}
                onChange={(e) => handleInputChange('DebitAccountId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {accounts.map((account) => (
                  <option key={account.Oid} value={account.Oid}>
                    {account.Code} - {account.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* CreditAccount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Кт. Данс
              </label>
              <select
                value={formData.CreditAccountId || ''}
                onChange={(e) => handleInputChange('CreditAccountId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {accounts.map((account) => (
                  <option key={account.Oid} value={account.Oid}>
                    {account.Code} - {account.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* VATAccount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                НӨАТ Данс
              </label>
              <select
                value={formData.VATAccountId || ''}
                onChange={(e) => handleInputChange('VATAccountId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {accounts.map((account) => (
                  <option key={account.Oid} value={account.Oid}>
                    {account.Code} - {account.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* CityTaxAccount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ХТ Данс
              </label>
              <select
                value={formData.CityTaxAccountId || ''}
                onChange={(e) => handleInputChange('CityTaxAccountId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {accounts.map((account) => (
                  <option key={account.Oid} value={account.Oid}>
                    {account.Code} - {account.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* BusinessClass */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Бизнес ангилал
              </label>
              <select
                value={formData.BusinessClassOid || ''}
                onChange={(e) => handleInputChange('BusinessClassOid', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {businessClasses.map((businessClass) => (
                  <option key={businessClass.Oid} value={businessClass.Oid}>
                    {businessClass.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Харилцагч
              </label>
              <select
                value={formData.CustomerOid || ''}
                onChange={(e) => handleInputChange('CustomerOid', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {customers.map((customer) => (
                  <option key={customer.Oid} value={customer.Oid}>
                    {customer.CustomerName} ({customer.CustomerID})
                  </option>
                ))}
              </select>
            </div>

            {/* BusinessCustomer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Бизнес харилцагч
              </label>
              <select
                value={formData.BusinessCustomerOid || ''}
                onChange={(e) => handleInputChange('BusinessCustomerOid', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Сонгох...</option>
                {businessCustomers.map((businessCustomer, index) => (
                  <option key={`business-${index}`} value={businessCustomer.Code}>
                    {businessCustomer.Code} - {businessCustomer.Name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Цуцлах
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Хадгалж байна...' : 'Хадгалах'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateLedgerReceiptItemPopup;
